import { NavLink } from 'react-router-dom';
import { Home, Map, Plus, User } from 'lucide-react';
import { cn } from '@ridemaadi/utils';
import { APP_NAV_BASE_HEIGHT_PX } from './shellLayout';
import { useRideMode } from '../../features/rides/rideMode/useRideMode';

/** Shared circular action size for Create and SOS tabs. */
const ACTION_CIRCLE =
  'flex h-14 w-14 shrink-0 items-center justify-center rounded-full shadow-lg transition-transform active:scale-95';

const TAB_ITEMS = [
  { to: '/dashboard', label: 'Home', icon: Home },
  { to: '/explore', label: 'Explore', icon: Map },
] as const;

function NavTab({
  to,
  label,
  icon: Icon,
}: {
  to: string;
  label: string;
  icon: typeof Home;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex min-h-touch w-full flex-col items-center justify-center gap-1 py-2 text-xs transition-colors',
          isActive ? 'font-semibold text-primary' : 'text-muted hover:text-content',
        )
      }
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden />
      <span>{label}</span>
    </NavLink>
  );
}

/** Fixed bottom navigation - SOS during active rides, otherwise Profile. */
export function BottomNavigation() {
  const { isRideMode, sosActive, openSos } = useRideMode();

  return (
    <nav
      aria-label="Primary"
      className="shrink-0 border-t border-border/60 bg-surface/95 shadow-[0_-4px_24px_rgb(0_0_0/0.06)] backdrop-blur-md dark:shadow-[0_-4px_24px_rgb(0_0_0/0.25)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul
        className="mx-auto grid max-w-app grid-cols-4 items-end px-1"
        style={{ minHeight: APP_NAV_BASE_HEIGHT_PX }}
      >
        {TAB_ITEMS.map((item) => (
          <li key={item.to} className="flex justify-center">
            <NavTab {...item} />
          </li>
        ))}

        <li className="flex justify-center">
          <NavLink
            to="/rides/new"
            className="group flex w-full flex-col items-center gap-1 py-2 text-primary"
            aria-label="Create a new ride"
          >
            <span
              className={cn(
                ACTION_CIRCLE,
                'bg-primary text-primary-fg shadow-primary/35',
              )}
            >
              <Plus className="h-7 w-7 stroke-[2.5]" aria-hidden />
            </span>
            <span className="text-xs font-semibold">Create</span>
          </NavLink>
        </li>

        <li className="flex justify-center">
          {isRideMode ? (
            <button
              type="button"
              onClick={openSos}
              className="flex w-full flex-col items-center gap-1 py-2 text-xs font-semibold text-danger"
              aria-label={sosActive ? 'SOS active - view emergency status' : 'Open emergency SOS'}
            >
              <span
                className={cn(
                  ACTION_CIRCLE,
                  'bg-danger text-[11px] font-bold text-white shadow-danger/40',
                  sosActive && 'rm-sos-active bg-red-900',
                )}
              >
                SOS
              </span>
              <span>SOS</span>
            </button>
          ) : (
            <NavTab to="/profile" label="Profile" icon={User} />
          )}
        </li>
      </ul>
    </nav>
  );
}
